<template>
  <div class="contenedor-form-general">
    <div class="fondologin"></div>
    <LogoForms />

    <!-- Estado cargando -->
    <div v-if="loading" class="contenedor-form">
      <p class="font-bold titulo">Registro de empresa</p>
      <p>Cargando…</p>
    </div>

    <!-- Registro no habilitado -->
    <div v-else-if="!enabled && !success" class="contenedor-form">
      <p class="font-bold titulo">Registro de empresa</p>
      <div class="p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">
        Este evento <strong>no tiene habilitado</strong> el registro público.
      </div>
      <button @click="goHome">Volver</button>
    </div>

    <!-- Éxito: ocultar formulario y mostrar CTA con magic link -->
    <div v-else-if="success" class="contenedor-form">
      <p class="font-bold titulo">¡Registro completado!</p>

      <div class="p-4 rounded-2xl bg-green-50 border border-green-200 text-green-800 text-sm">
        <p class="mb-2">
          Tu empresa y tu usuario han sido registrados correctamente para
          <strong>{{ eventName || 'el evento' }}</strong>.
        </p>
        <p>
          Te hemos enviado un correo de confirmación con tu enlace de acceso.
          También puedes ingresar dando clic en el siguiente botón:
        </p>
      </div>

      <a
        v-if="magicLink"
        :href="magicLink"
        class="mt-4 block text-center px-5 py-3 rounded-3xl font-semibold bg-black/80 text-white hover:bg-black"
      >
        Acceder a la plataforma
      </a>

      <p v-else class="text-sm text-gray-700 mt-4">
        (Si no ves el botón, revisa tu bandeja de entrada: hemos enviado tu enlace de acceso).
      </p>

      <button class="mt-4" @click="goHome">Ir al inicio</button>
    </div>

    <!-- Formulario -->
    <div v-else class="contenedor-form">
      <p class="font-bold titulo">Registro para {{ eventName || 'el evento' }}</p>

      <form @submit.prevent="submitForm">
        <!-- Empresa -->
        <div class="correo">
          <span>Razón social</span>
          <input v-model="empresa.razon_social" type="text" placeholder="Nombre legal de la empresa" />
        </div>

        <div class="md:flex w-full">
          <div class="correo w-full md:w-1/4">
            <span>Tipo identificación</span>
            <select v-model="empresa.tipo_identificacion">
              <option :value="null">—</option>
              <option value="CC">CC</option>
              <option value="NIT">NIT</option>
              <option value="PASAPORTE">PASAPORTE</option>
            </select>
          </div>

          <div class="correo w-full md:w-3/4 md:pl-5">
            <span>Número identificación</span>
            <input v-model="empresa.numero_identificacion" type="text" />
          </div>
        </div>

        <div class="md:flex w-full">
          <div class="correo w-full md:w-1/2 md:pr-3">
            <span>País</span>
            <input v-model="empresa.pais" type="text" />
          </div>

          <div class="correo w-full md:w-1/2 md:pl-3">
            <span>Ciudad</span>
            <input v-model="empresa.ciudad" type="text" />
          </div>
        </div>

        <div class="md:flex w-full">
          <div class="correo w-full md:w-1/2 md:pr-3">
            <span>Dirección</span>
            <input v-model="empresa.direccion" type="text" />
          </div>
          <div class="correo w-full md:w-1/2 md:pl-3">
            <span>Teléfono</span>
            <input v-model="empresa.telefono" type="text" />
          </div>
        </div>

        <div class="md:flex w-full">
          <div class="correo w-full md:w-1/2 md:pr-3">
            <span>Página web</span>
            <input v-model="empresa.pagina_web" type="text" />
          </div>

          <!-- Representante / Usuario -->
          <div class="correo w-full md:w-1/2 md:pl-3">
            <span>¿Eres representante legal?</span>
            <select v-model="representante.es_representante">
              <option :value="true">Sí</option>
              <option :value="false">No</option>
            </select>
          </div>
        </div>

        <hr class="my-5" />

        <div class="md:flex w-full">
          <div class="correo w-full md:w-1/2 md:pr-3">
            <span>Nombre</span>
            <input v-model="representante.nombre" type="text" placeholder="Nombre completo" />
          </div>
          <div class="correo w-full md:w-1/2 md:pl-3">
            <span>Correo electrónico</span>
            <input v-model="representante.correo_electronico" type="email" placeholder="ejemplo@mail.com" />
          </div>
        </div>

        <template v-if="representante.es_representante">
          <div class="md:flex w-full">
            <div class="correo w-full md:w-1/3 md:pr-3">
              <span>Celular</span>
              <input v-model="representante.celular" type="text" />
            </div>
            <div class="correo w-full md:w-1/3 md:pl-3">
              <span># Identificación</span>
              <input v-model="representante.identificacion" type="text" />
            </div>
            <div class="correo w-full md:w-1/3 md:pl-3">
              <span>Cargo</span>
              <input v-model="representante.cargo" type="text" />
            </div>
          </div>
        </template>

        <button type="submit" :disabled="submitting">
          {{ submitting ? 'Enviando…' : 'Enviar registro' }}
        </button>
      </form>

      <p class="terminos">
        Al enviar aceptas las <a href="/condiciones-de-uso">Condiciones de Uso</a> y el
        <a href="/aviso-de-privacidad">Aviso de privacidad</a>.
      </p>
    </div>


  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import LogoForms from '../login/LogoForms/LogoForms.vue'

const toast = useToast()
const route = useRoute()
const router = useRouter()

const token = route.params.token

const loading    = ref(true)
const enabled    = ref(false)
const eventName  = ref('')
const submitting = ref(false)

/* NUEVO: estados de éxito y magic link */
const success    = ref(false)
const magicLink  = ref('')

const empresa = ref({
  tipo_identificacion: null,
  numero_identificacion: null,
  razon_social: '',
  pais: '',
  ciudad: '',
  direccion: '',
  telefono: '',
  moneda: 'USD',
  pagina_web: '',
})

const representante = ref({
  es_representante: false,
  nombre: '',
  correo_electronico: '',
  celular: '',
  identificacion: '',
  cargo: ''
})

function goHome() { router.push('/') }

function validEmail(e) {
  const re = /^([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)@(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})$/
  return re.test(String(e).toLowerCase())
}

async function init() {
  try {
    const res = await fetch(`/api/registro-control/init/${encodeURIComponent(token)}`)
    const data = await res.json()
    if (!data?.ok) throw new Error('Respuesta inválida')
    enabled.value = !!data.enabled
    eventName.value = data.event_name || ''
  } catch (e) {
    toast.error('No se pudo validar el evento.')
  } finally {
    loading.value = false
  }
}

async function submitForm() {
  // Validaciones mínimas
  if (!empresa.value.razon_social) {
    toast.warning('Ingresa la razón social.')
    return
  }
  if (!representante.value.nombre || !representante.value.correo_electronico) {
    toast.warning('Ingresa nombre y correo del contacto.')
    return
  }
  if (!validEmail(representante.value.correo_electronico)) {
    toast.warning('Correo inválido.')
    return
  }

  submitting.value = true
  try {
    const payload = {
      tokenEvento: token,
      empresa: { ...empresa.value },
      representante: { ...representante.value }
    }

    const res = await fetch('/api/registro-control/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const data = await res.json()
    if (data?.ok) {
      // mostrar tarjeta de éxito y CTA
      success.value   = true
      magicLink.value = data.magic_link || ''

      toast.success('Registro enviado correctamente.')
      // Limpieza opcional de formulario (ya no se ve, pero queda limpio)
      empresa.value = {
        tipo_identificacion: null,
        numero_identificacion: null,
        razon_social: '',
        pais: '',
        ciudad: '',
        direccion: '',
        telefono: '',
        moneda: 'USD',
        pagina_web: '',
      }
      representante.value = {
        es_representante: false,
        nombre: '',
        correo_electronico: '',
        celular: '',
        identificacion: '',
        cargo: ''
      }
    } else {
      const reason = data?.reason || 'No se pudo completar el registro.'
      toast.error(reason)
    }
  } catch (e) {
    toast.error('Error de red. Intenta nuevamente.')
  } finally {
    submitting.value = false
  }
}

onMounted(init)
</script>

<style lang="sass" scoped>
/* Reutiliza el estilo del primer componente */
.fondologin
  top: 0
  position: fixed
  min-height: 150vh
  min-width: 100vw
  z-index: -2
  background-repeat: no-repeat
  background-attachment: fixed
  background-size: cover
  background-position: top
  background-image: url(~@/assets/forms/jakob-dalbjorn-cuKJre3nyYc-unsplash.jpg)!important

.contenedor-form-general
  width: 95vw
  margin: 0 auto
  max-width: 800px
  padding: 20px 20px 20px 20px
  display: flex
  align-items: center
  flex-direction: column
  justify-content: center
  height: 100%
  border-radius: 20px
  position: relative

  .contenedor-form, .caja-blanca
    padding: 20px 25px 15px 25px
    backdrop-filter: blur(10px)
    background-color: rgba(255, 255, 255, 0.35)
    border-radius: 20px
    width: 100%
    margin-top: 20px
    .titulo
      font-size: 32px
      text-align: left
      margin-bottom: 20px

    form
      position: relative
      text-align: left
      .correo
        span
          display: block
          width: 100%
          margin: 0
          padding: 0
          font-size: 20px
          font-weight: bold
          margin: 5px 0 6px 0
        input
          width: 100%
          border: 2px solid rgba(120,120,120, 0.6)
          padding: 4px 15px
          border-radius: 30px
          background: rgba(255,255,255, 0.5)
          transition: all 0.2s
          margin-bottom: 10px
          &:focus, &:active
            background: #fff
            outline: none
            border: 2px solid rgba(200,200,200, 1)
            box-shadow: 0 0 3px rgba(155,155,155, 0.8)
        select
          width: 100%
          border: 2px solid rgba(120,120,120, 0.6)
          padding: 7px 15px
          border-radius: 30px
          background: rgba(255,255,255, 0.5)
          transition: all 0.2s
          margin-bottom: 10px
          &:focus, &:active
            background: #fff
            outline: none
            border: 2px solid rgba(200,200,200, 1)
            box-shadow: 0 0 3px rgba(155,155,155, 0.8)

    button
      display: block
      width: 100%
      border: none
      padding: 15px
      text-align: center
      cursor: pointer
      font-size: 16px
      background: rgba(255,255,255, 0.6)
      color: #000
      margin-top: 15px
      font-size: 0.8em
      border-radius: 30px
      transition: all 0.2s
      &:hover
        background: rgba(255,255,255, 0.9)
        transform: scale(0.98)
        box-shadow: 0 0 5px rgba(155,155,155, 0.5)
      &:active
        transform: scale(0.95)
        border: 1px solid rgba(0,0,0, 0.7)

  .terminos
    font-size: 0.7em
    max-width: 100%
    margin: 20px auto
    a
      color: #555
      text-decoration: underline
      font-weight: bold
      transition: all 0.2s
      &:hover
        color: #000
</style>
